import { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { EApiRoutes } from 'api/api.utils';
import { useAllowedProjects } from 'api/useAllowedProjects';
import { useCreateFirstProject } from 'api/useCreateFirstProject';
import usePermissions from 'api/usePermissions';
import { useUserDetails } from 'api/useUserDetails';
import { AxiosError } from 'axios';
import { EChecklistTaskStatus, ETaskId } from 'common/contracts';
import { EEventsType, ELocalStorageKeys } from 'constants/enums';
import { useBigQueryEventsTracking } from 'hooks/useBigQueryTracking';
import { useSupportHandler } from 'pages/Layout/ProfileDropDown/useSupportHandler';
import { localStorageUtil } from 'utils/localStorageUtil';
import { trackMixpanelEvent } from 'utils/mixpanelClient';
import { EToasterStatus, showToast } from 'utils/showToast';
import { z } from 'zod';

import Button from 'components/Button/Button';
import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import Typography from 'components/Typography/Typography';
import { Form, FormControl, FormField, FormItem } from 'components/ui';

import {
  ANNUAL_REVENUE_OPTIONS,
  countryOptions,
  EInitProjectFormTeamSize,
  INIT_PROJECT_FORM_SCHEMA,
  TEAM_SIZE_OPTIONS
} from './constants';

export const CreateFirstProjectForm: React.FC = () => {
  const [publisherId, setPublisherId] = useState<string | undefined>(undefined);
  const { initFirstProject } = useCreateFirstProject();
  const allowedProjectsQuery = useAllowedProjects(publisherId);
  const navigate = useNavigate();
  const { setPermissions } = usePermissions();
  const { onSupportClick } = useSupportHandler();
  const { trackBigQueryEvent } = useBigQueryEventsTracking();
  const { userDetails } = useUserDetails();
  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof INIT_PROJECT_FORM_SCHEMA>>({
    resolver: zodResolver(INIT_PROJECT_FORM_SCHEMA),
    mode: 'onChange',
    defaultValues: {
      businessName: '',
      websiteUrl: '',
      country: '',
      teamSize: EInitProjectFormTeamSize.ONE_TO_FIVE,
      annualRevenue: ''
    }
  });

  const {
    formState: { errors, isValid }
  } = form;

  const onSubmit = async (data: z.infer<typeof INIT_PROJECT_FORM_SCHEMA>) => {
    const countryCode3 =
      countryOptions.find((opt) => opt.value === data.country)?.code ?? '';

    initFirstProject.mutate(
      { ...data, countryCode3 },
      {
        onSuccess: (responseData: any) => {
          const {
            data: { _id: newPublisherId }
          } = responseData;
          trackMixpanelEvent(EEventsType.DASHBOARD_BUSINESS_INFO_SUBMITTED, {
            company_id: newPublisherId,
            business_name: data.businessName,
            website_url: data.websiteUrl,
            country_code: data.country,
            team_size: data.teamSize,
            annual_rev: data.annualRevenue,
            publisher_id: newPublisherId
          });
          trackBigQueryEvent({
            eventName: EEventsType.DASHBOARD_BUSINESS_INFO_SUBMITTED,
            publisherId: newPublisherId,
            websiteUrl: data.websiteUrl,
            countryCode: data.country,
            teamSize: data.teamSize,
            annualRev: data.annualRevenue,
            taskName: ETaskId.VERIFY_BUSINESS_CONTACT,
            taskStatus: EChecklistTaskStatus.DONE
          });
          showToast({
            message: 'Project created successfully!',
            status: EToasterStatus.SUCCESS,
            description: 'You will be redirected soon'
          });
          setPublisherId(newPublisherId);
          localStorageUtil.set(ELocalStorageKeys.PUBLISHER_ID, newPublisherId);
        },
        onError: (error: any) => {
          const axiosError = error as AxiosError;

          if (axiosError?.response?.status === 405) {
            navigate('/init-project/request-submitted');
            return;
          }

          showToast({
            message: 'Project creation failed. Please try again.',
            status: EToasterStatus.ERROR,
            description:
              (axiosError?.response?.data as { [key: string]: any })?.message ??
              'Unknown error occurred'
          });
        }
      }
    );
  };

  useEffect(() => {
    if (allowedProjectsQuery?.data?.projects?.length > 0) {
      const updatedUserDetails = {
        ...userDetails,
        projects: allowedProjectsQuery.data.projects,
        isOnboarding: allowedProjectsQuery.data.isOnboarding
      };
      // Update React Query cache instead of localStorage
      queryClient.setQueryData([EApiRoutes.USERS], updatedUserDetails);
      setPermissions();
      navigate(`/project/${publisherId}/orders`);
    }
  }, [allowedProjectsQuery.data, navigate, publisherId, setPermissions]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="w-[374px] px-4 lg:px-5 lg:pt-5 lg:pb-[14px] mt-2.5 lg:my-[26px]"
      >
        <Typography className="text-center lg:text-left" tag="h3">
          Add company details
        </Typography>
        <Typography className="text-p-s mt-3 mb-10 text-center lg:text-left text-zinc-500">
          Our publisher registration process takes up to 24 hours, allowing you
          to get started in no-time.
        </Typography>
        <div
          className="flex flex-col w-full justify-between"
          style={{ flexGrow: '1' }}
        >
          <div className="flex flex-col gap-y-4">
            <FormField
              control={form.control}
              name="businessName"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      isRequired
                      label="Business Legal Name"
                      placeholder="Business name"
                      state={errors.businessName?.message ? 'error' : 'default'}
                      description={errors.businessName?.message}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="websiteUrl"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      isRequired
                      label="Website URL"
                      placeholder="URL"
                      state={errors.websiteUrl?.message ? 'error' : 'default'}
                      description={errors.websiteUrl?.message}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 items-center gap-x-4">
              <FormField
                control={form.control}
                name="country"
                render={({ field: { onChange } }) => (
                  <FormItem>
                    <FormControl>
                      <InputSelectDropdown
                        isRequired
                        label="Country"
                        placeholder="Select country"
                        defaultSelected={
                          countryOptions.find(
                            (opt) => opt.value === form.watch('country')
                          ) || { value: '', label: '' }
                        }
                        onOptionChange={onChange}
                        options={countryOptions}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="teamSize"
                render={({ field: { onChange } }) => (
                  <FormItem>
                    <FormControl>
                      <InputSelectDropdown
                        isRequired
                        label="Team Size"
                        placeholder="1-5"
                        defaultSelected={TEAM_SIZE_OPTIONS.find(
                          (opt) => opt.value === form.watch('teamSize')
                        )}
                        onOptionChange={onChange}
                        options={TEAM_SIZE_OPTIONS}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="annualRevenue"
              render={({ field: { onChange } }) => (
                <FormItem>
                  <FormControl>
                    <InputSelectDropdown
                      label="Annual Revenue"
                      placeholder="$0–50K"
                      defaultSelected={countryOptions.find(
                        (opt) => opt.value === form.watch('annualRevenue')
                      )}
                      onOptionChange={onChange}
                      options={ANNUAL_REVENUE_OPTIONS}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <Button disabled={!isValid} type="submit" className="w-full">
              Continue
            </Button>
          </div>
          <Typography className="text-p-s text-zinc-500 mt-10 text-center">
            Need help?{' '}
            <Button
              variant="link"
              className="font-bold underline p-0 text-p-s text-zinc-500 h-4"
              onClick={onSupportClick}
            >
              Contact support
            </Button>
          </Typography>
        </div>
      </form>
    </Form>
  );
};
